import { create } from 'zustand'
import { friendlyMessage } from '@/api/http'
import { jobApi, type JobListOptions } from '@/api/jobApi'
import { openSSE } from '@/api/sse'
import type { JobStatus, JobView } from '@/types/nf'

interface JobState {
  jobs: Record<string, JobView>
  loading: boolean
  error: string | null
  loadList: (projectId: string, opts?: JobListOptions) => Promise<void>
  upsert: (view: JobView) => void
  subscribe: (jobId: string) => () => void
  unsubscribe: (jobId: string) => void
  unsubscribeAll: () => void
}

const subscriptions = new Map<string, () => void>()

const terminalStatuses: JobStatus[] = ['done', 'failed', 'needs_review', 'partial_result']

function getErrorMessage(err: unknown) {
  return friendlyMessage(err)
}

function parseStreamError(data: unknown) {
  if (data && typeof data === 'object') {
    const payload = data as Record<string, unknown>
    if (typeof payload.error_message === 'string') return payload.error_message
    if (typeof payload.error === 'string') return payload.error
  }
  return 'SSE job stream error'
}

function maybeStatus(value: unknown): JobStatus | null {
  if (typeof value !== 'string') return null
  return terminalStatuses.includes(value as JobStatus) ? (value as JobStatus) : null
}

function parseDonePatch(data: unknown): Partial<JobView> {
  if (!data || typeof data !== 'object') return {}
  const payload = data as Record<string, unknown>
  const patch: Partial<JobView> = {}
  const chapterId = payload.result_chapter_id ?? payload.chapter_id
  if (typeof chapterId === 'string') patch.result_chapter_id = chapterId
  if (typeof payload.finished_at === 'number') patch.finished_at = payload.finished_at
  return patch
}

function reconcile(jobId: string) {
  void jobApi
    .get(jobId)
    .then((view) => useJobStore.getState().upsert(view))
    .catch((err) => useJobStore.setState({ error: getErrorMessage(err) }))
}

export const useJobStore = create<JobState>((set, get) => ({
  jobs: {},
  loading: false,
  error: null,

  loadList: async (projectId, opts) => {
    set({ loading: true, error: null })
    try {
      const data = await jobApi.list(projectId, opts)
      set((state) => {
        const jobs = Object.fromEntries(Object.entries(state.jobs).filter(([, item]) => item.project_id !== projectId))
        for (const item of data.items) jobs[item.id] = item
        return { jobs, loading: false }
      })
    } catch (err) {
      set({ error: getErrorMessage(err), loading: false })
    }
  },

  upsert: (view) => {
    set((state) => ({ jobs: { ...state.jobs, [view.id]: view } }))
  },

  subscribe: (jobId) => {
    get().unsubscribe(jobId)

    const close = openSSE(
      `/v1/chapters/jobs/${encodeURIComponent(jobId)}/stream`,
      {
        onReconnect: () => {
          reconcile(jobId)
        },
        onError: (err) => {
          set({ error: friendlyMessage(err) })
          get().unsubscribe(jobId)
        },
        onEvent: (event, data) => {
          if (event === 'status') {
            get().upsert(data as JobView)
            return
          }

          if (event === 'segment_done') {
            const seg = data && typeof data === 'object' ? (data as Record<string, unknown>).seg : undefined
            if (typeof seg === 'number') {
              set((state) => {
                const current = state.jobs[jobId]
                if (!current) return state
                return { jobs: { ...state.jobs, [jobId]: { ...current, current_seg: Math.max(current.current_seg, seg) } } }
              })
            }
            return
          }

          if (event === 'done') {
            set((state) => {
              const current = state.jobs[jobId]
              const jobs = current ? { ...state.jobs, [jobId]: { ...current, ...parseDonePatch(data), status: 'done' as JobStatus } } : state.jobs
              return { jobs }
            })
            get().unsubscribe(jobId)
            void jobApi
              .get(jobId)
              .then((view) => get().upsert(view))
              .catch((err) => set({ error: getErrorMessage(err) }))
            return
          }

          if (event === 'error') {
            const message = parseStreamError(data)
            const parsedStatus = data && typeof data === 'object' ? maybeStatus((data as Record<string, unknown>).status) : null
            const finalStatus = parsedStatus ?? 'failed'
            set((state) => {
              const current = state.jobs[jobId]
              const jobs = current ? { ...state.jobs, [jobId]: { ...current, status: finalStatus, error_message: message } } : state.jobs
              return { jobs, error: message }
            })
            get().unsubscribe(jobId)
          }
        },
      },
      { terminalEvents: ['done', 'error'] },
    )

    subscriptions.set(jobId, close)
    return () => get().unsubscribe(jobId)
  },

  unsubscribe: (jobId) => {
    const close = subscriptions.get(jobId)
    if (!close) return
    subscriptions.delete(jobId)
    close()
  },

  unsubscribeAll: () => {
    for (const close of subscriptions.values()) close()
    subscriptions.clear()
  },
}))
